/*
 * Tool names, said the way an operator would say them.
 *
 * The window shows what it is doing while it works, and "get_wan_quality" is
 * not what anyone is doing — it is how the call is spelled. Names are derived
 * rather than enumerated so a new tool reads sensibly on the day it is added
 * instead of falling through to its raw identifier.
 *
 * The vocabulary is also the product's, not the codebase's: an operator has
 * links and concealment, not WANs and obfuscation.
 */

const VERBS = {
  get: "Reading",
  propose: "Preparing",
  draw: "Drawing",
  compose: "Writing up",
};

/* Whole names worth saying properly, where the derived version would be vague. */
const EXACT = {
  compose_answer: "Writing up the answer",
  draw_topology: "Drawing the data path",
  draw_pipeline: "Drawing the stages",
  draw_comparison: "Drawing the comparison",
  record_finding: "Writing this down for next time",

  get_mode: "Checking which mode this edge is in",
  get_wan_links: "Listing the uplinks",
  get_wan_health: "Measuring latency, jitter and loss",
  get_wan_ranking: "Checking which link is preferred",
  get_wan_capacity: "Estimating what each link can carry",
  get_policy: "Reading how each class is routed",
  get_traffic_classes: "Listing the traffic classes",
  get_tunnel_stats: "Counting what the tunnel has carried",
  get_class_wan_stats: "Attributing traffic to links",
  get_encryption_status: "Checking what is encrypted",
  get_obfuscation_status: "Checking the concealment layers",
  get_qos_thresholds: "Reading the per-class quality rules",
  get_qos_status: "Checking which classes are breaching",
  get_probing_config: "Reading how often links are checked",
  get_failover_thresholds: "Reading when a link is given up on",
  get_aqm_status: "Checking whether anything is shaping traffic",
  get_diagnostics: "Reading appliance diagnostics",
  get_system_diagnostics: "Checking the appliance itself",
  get_tunnel_diagnostics: "Checking the tunnel",
  get_dataplane_diagnostics: "Reading the data path counters",
  get_events: "Reading the event log",
  get_qos_events: "Looking for recent quality breaches",
  get_lan_devices: "Listing devices on the network",
  get_classifier_flows: "Sampling what has been identified",
  get_classifier_diag: "Checking traffic identification",
  get_as_path: "Tracing which networks the traffic crosses",
  get_worklog: "Checking what was done here before",
  get_cloud_anchors: "Listing the available exits",
  get_wan_quality: "Reading the quality forecast",
};

/* Product vocabulary. Anything absent keeps its own word. */
const WORDS = {
  wan: "link",
  wans: "links",
  lan: "network",
  qos: "quality rule",
  aqm: "queue shaping",
  tc: "shaping",
  obfuscation: "concealment",
  mtd: "rotation",
  chaff: "cover traffic",
  seqforecast: "quality prediction",
  classifier: "traffic labelling",
  enc: "encryption",
  cloud: "anchor",
  tunnel: "tunnel",
  wire: "concealment",
  blend: "link spreading",
  blending: "link spreading",
};

/** stepLabel renders one tool call as the thing it is doing, in plain words. */
export function stepLabel(name) {
  if (!name) return "Working";
  if (EXACT[name]) return EXACT[name];

  const [verb, ...rest] = name.split("_");
  const lead = VERBS[verb] || "Checking";
  if (!rest.length) return lead;

  const phrase = rest
    .map((w) => WORDS[w] || w)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return phrase ? `${lead} ${phrase}` : lead;
}
